import {assert, assertAndContinue} from 'Asserts';
import {
  Container,
  AccessibleGridCell,
  GridCellSummary,
  isAccessibleGridCell,
} from 'constants/SampleConstants';
import AddSamplesFromBoxOrPlateModal from 'notebook/sample-propagation/AddSamplesFromBoxOrPlateModal';
import {
  AddSamplesToTableTrackingParams,
  trackAddSamples,
} from 'notebook/sample-propagation/util/addSamplesTracking';
import {
  StructuredTableAddOperationOptions,
  UnknownStructuredTable,
  UnknownStructuredTableAdapter,
} from 'notebook/structured-tables/StructuredTableConstants';
import {Row} from 'notebook/tables/constants/RowConstants';
import {TableColumnType} from 'notebook/tables/constants/TableColumnConstants';
import {BaseTypedValueType} from 'notebook/tables/typed-values/TypedValueConstants';
import * as SampleUtil from 'samples/SampleUtil';
import legacyRunModal from 'ui-library/legacyRunModal';
import APIUtil from 'util/APIUtil';

import {dedupeSampleFilters, SampleFilterType} from './SampleFilter';
import getAcceptableSampleFiltersByColumn from './util/getAcceptableSampleFiltersByColumn';

/**
 * Allows the user to add rows to a table from a box or plate. This function handles the entire
 * operation, from opening the "select parent storable and grid cells" modal through to adding the
 * rows to the table.
 */

export default async function addSamplesFromBoxOrPlate(
  destinationTable: UnknownStructuredTable,
  adapter: UnknownStructuredTableAdapter,
  // TODO(BNCH-41013): use addSamples instead of addFullRows to avoid overwriting locked cell values
  addFullRows: (rows: Array<Row>) => void,
  destinationSpec: {
    destinationSchemaId: string | null;
    destinationSchemaName: string | null;
    destinationColumnType: TableColumnType;
    registryId: string | null;
  },
  options?: StructuredTableAddOperationOptions,
  trackingParams?: AddSamplesToTableTrackingParams
): Promise<void> {
  const convertedTable = adapter.toTable(destinationTable);
  const destinationSampleFiltersByColumn = getAcceptableSampleFiltersByColumn(convertedTable)
    .map((sampleTypeByColumn) => ({
      ...sampleTypeByColumn,
      columns: sampleTypeByColumn.columnIndices.map((index) => destinationTable.columns[index]),
    }))
    .filter(
      ({columns}) => columns.length === 1 && columns[0].columnType === destinationSpec.destinationColumnType
    ) // Only allow columns that match specified column type
    .filter(({sampleFilters}) =>
      sampleFilters.every(
        ({schemaId}) =>
          schemaId == null ||
          destinationSpec.destinationSchemaId == null ||
          schemaId === destinationSpec.destinationSchemaId
      )
    ); // Only allow filters that match specified destinationSchemaId (or null)
  const acceptableSampleTypes = dedupeSampleFilters(
    destinationSampleFiltersByColumn.flatMap(({sampleFilters}) => sampleFilters)
  );
  assert(acceptableSampleTypes.length === 1, 'Only one valid type expected');
  const sampleType = acceptableSampleTypes[0].type;

  const addSamplesCommand = await legacyRunModal(AddSamplesFromBoxOrPlateModal, {
    destinationColumnType: destinationSpec.destinationColumnType,
    destinationSchemaId: destinationSpec.destinationSchemaId,
    destinationSchemaName: destinationSpec.destinationSchemaName,
    registryId: destinationSpec.registryId,
  });
  if (addSamplesCommand) {
    const {selectedParent, selectedCells} = addSamplesCommand;
    let rows: Array<Row> = [];

    switch (destinationSpec.destinationColumnType) {
      case TableColumnType.CONTAINERS: {
        assert(
          sampleType === SampleFilterType.CONTAINER,
          'Expected matching container sample filter type for container destination column'
        );

        const containerColumnIndex = destinationTable.columns.findIndex(
          ({columnType}) => columnType === TableColumnType.CONTAINERS
        );
        assert(containerColumnIndex != null, 'Destination table should have a valid containers column');

        const nextContainerColumnIndex = destinationTable.columns.findIndex(
          ({columnType}, columnIndex) =>
            columnType === TableColumnType.CONTAINERS && columnIndex !== containerColumnIndex
        );
        assert(
          nextContainerColumnIndex === -1,
          'Destination table should not have multiple containers column'
        );

        rows = selectedCells
          .filter((cell: GridCellSummary) => isAccessibleGridCell(cell))
          .map((cell: AccessibleGridCell) => cell.container)
          .map((container: Container) => [
            ...[...Array(containerColumnIndex)].map((_) => ({text: ''})),
            {
              typedValue: {
                type: BaseTypedValueType.STORABLES,
                storableSummaries: [{barcode: container.barcode, id: container.id, name: container.name}],
              },
              text: '',
            },
            ...[...Array(destinationTable.columns.length - containerColumnIndex - 1)].map((_) => ({
              text: '',
            })),
          ]);

        addFullRows(rows);
        break;
      }
      case TableColumnType.PLATE: {
        assert(
          sampleType === SampleFilterType.PLATE,
          'Expected matching plate sample filter type for plate destination column'
        );

        const plateColumnIndex = destinationTable.columns.findIndex(
          ({columnType}) => columnType === TableColumnType.PLATE
        );
        const positionColumnIndex = destinationTable.columns.findIndex(
          ({columnType}) => columnType === TableColumnType.POSITION
        );
        assert(
          plateColumnIndex != null && positionColumnIndex != null,
          'Destination table should have valid plate and position columns'
        );

        const nextPlateColumnIndex = destinationTable.columns.findIndex(
          ({columnType}, columnIndex) =>
            columnType === TableColumnType.PLATE && columnIndex !== plateColumnIndex
        );
        const nextPositionColumnIndex = destinationTable.columns.findIndex(
          ({columnType}, columnIndex) =>
            columnType === TableColumnType.POSITION && columnIndex !== positionColumnIndex
        );
        assert(
          nextPlateColumnIndex === -1 && nextPositionColumnIndex === -1,
          'Destination table should not have multiple plate and position columns'
        );

        const plate = selectedParent.storable!;
        rows = selectedCells.map((_) => [
          ...[...Array(plateColumnIndex)].map((_) => ({text: ''})),
          {
            typedValue: {
              type: BaseTypedValueType.STORABLES,
              storableSummaries: [
                {
                  barcode: plate.barcode?.value,
                  id: plate.id,
                  name: plate.name,
                },
              ],
            },
            text: '',
          },
          ...[...Array(destinationTable.columns.length - plateColumnIndex - 1)].map((_) => ({
            text: '',
          })),
        ]);

        selectedCells.forEach((cell: GridCellSummary, index) => {
          rows[index][positionColumnIndex] = {
            text: SampleUtil.formatGridCoordinates(cell.rowIndex, cell.columnIndex),
          };
        });

        addFullRows(rows);
        break;
      }
      default:
        assertAndContinue(
          false,
          `Unsupported destination column type ${destinationSpec.destinationColumnType}`
        );
    }
    if (trackingParams) {
      trackAddSamples({
        ...trackingParams,
        numSamples: rows.length,
        sourceId: selectedParent.storable!.id,
        sourceType: APIUtil.isPlate(selectedParent.storable!.id) ? 'plate' : 'box',
      });
    }
  }
}
